import { useEffect, useRef, useState } from 'react'
import * as yup from 'yup'
import Footer from '@app/components/Footer/Footer'
import Navbar from '@app/components/Navbar/Navbar'
import TextField from '@app/components/TextField/TextField'
import SelectField from '@app/components/SelectField/SelectField'
import UploadField from '@app/components/UploadField/UploadField'
import Button from '@app/components/Button/Button'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import { QuizStep, updateQuiz } from '@app/store/slices/quiz'
import Tippy from '@tippyjs/react'
import { useGetUploadUrlQuery, usePutFileMutation } from '@app/store/api/customer'
import Flex from '@app/backoffice/Flex/Flex'
import Spinner from '@app/components/Spinner'
import Resizer from 'react-image-file-resizer'
import programs from '@app/programs'
import { Icon } from '@iconify/react'
import { format } from 'date-fns'
import useCampaignHref from '@app/hooks/useCampaignHref'
import S from './styles'
import QuizHeader from './components/QuizHeader/QuizHeader'
import { allowedFileTypes, validate, fileTypes } from './constants'

import SerialNumberHelper from './components/SerialNumberHelper'
import useQuizRedirect from './useQuizRedirect'
import RedirectPartialView from './RedirectPartialView'

const PurchaseCheck = () => {
  const navigate = useNavigate()
  const { program } = useParams<{ program: string }>()
  const dispatch = useAppDispatch()
  const { messages } = useIntl()
  const quiz = useAppSelector(state => state.quiz)
  const [getUrls, setGetUrls] = useState(false)
  const [showHelper, setShowHelper] = useState(false)

  const { href: quizValuationHref } = useCampaignHref({ requestPath: 'quiz/valuation' })

  const { isRedirecting } = useQuizRedirect({ requestedStep: QuizStep.PURCHASE })

  const presignedUrl1 = useGetUploadUrlQuery(
    { userId: quiz.customerId, type: fileTypes.serialNumber },
    { skip: !getUrls, refetchOnMountOrArgChange: true }
  )
  const presignedUrl2 = useGetUploadUrlQuery(
    { userId: quiz.customerId, type: fileTypes.receipt },
    { skip: !getUrls, refetchOnMountOrArgChange: true }
  )
  const [uploadSerial, uploadSerialRes] = usePutFileMutation()
  const [uploadReceipt, uploadReceiptRes] = usePutFileMutation()

  // Stores the previous validation value for the serial number
  const isPrevSerialNumberValid = useRef(false)

  // We validate the image size the user is uploading before we apply compression, we limit this to 10mb
  const isValidImage = (file: File): boolean => {
    // Check the file type
    if (!validate.fileUpload.includes(file.type)) {
      return false
    }
    // Check the file size (10MB max)
    if (file.size > 1024 * 1024 * 10) {
      return false
    }
    return true
  }

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      serialFile: undefined as File | undefined,
      receiptFile: undefined as File | undefined,
      serialNumber: quiz.purchaseSerial,
      purchaseDate: quiz.purchaseDate,
      purchaseStore: quiz.purchaseVendor,
    },
    validationSchema: yup.object({
      serialNumber: yup
        .string()
        .max(15, messages['static.form.error_serial_length'] as string)
        .matches(
          program ? validate.serialNumber[program as keyof typeof validate.serialNumber] : validate.serialNumber.fr,
          messages['static.form.error_serial_format'] as string
        )
        .test(
          'eligibility',
          messages['static.form.error_serial_format'] as string,
          async function checkSerialNumberEligibility(value) {
            // Value has not changed, return the previous validity
            if (formik.values.serialNumber === value) {
              return isPrevSerialNumberValid.current
            }
            // Bypass this check if the value is not the correct format
            if (!value || value.length < 15) {
              isPrevSerialNumberValid.current = true
              return true
            }
            // Run the eligibility check
            try {
              if (!program) throw new Error('Program not defined')
              const res = (await fetch(
                `${BASE_API_URL}/v1/devices/${value}/promo/${program.toUpperCase()}/eligibility`
              )) as any
              const data = (await res.json()) as { valid: boolean; processing: boolean }
              isPrevSerialNumberValid.current = data.valid
              return data.valid
            } catch (err) {
              isPrevSerialNumberValid.current = false
              return false
            }
          }
        )
        .required(messages['quiz_purchased_product_page.content.questions.serial_number.validation_message'] as string),
      purchaseDate: yup
        .string()
        .max(10, messages['static.form.error_date_invalid'] as string)
        .dateValid(messages['static.form.error_date_invalid'] as string)
        .inDateRange(
          programs[program!].startDate,
          programs[program!].endDate,
          (messages['static.form.error_date_range'] as string)
            ?.replace('{{START}}', format(programs[program!].startDate, 'dd/MM/yyyy'))
            ?.replace('{{END}}', format(programs[program!].endDate, 'dd/MM/yyyy'))
        )
        .required(
          messages['quiz_purchased_product_page.content.questions.date_of_purchase.validation_message'] as string
        ),
      purchaseStore: yup
        .string()
        .required(
          messages['quiz_purchased_product_page.content.questions.place_of_purchase.validation_message'] as string
        ),
      serialFile: yup
        .mixed<File>()
        .required(
          messages[
            'quiz_purchased_product_page.content.questions.serial_number_file_upload.validation_message'
          ] as string
        )
        .test('fileSize', messages['static.form.error_file_size'] as string, (value: File | Blob) => {
          if (value instanceof File || value instanceof Blob) {
            return value.size <= 1024 * 1024 * 10 // 10MB
          }
          return false
        }),
      receiptFile: yup
        .mixed<File>()
        .required(
          messages['quiz_purchased_product_page.content.questions.receipt_file_upload.validation_message'] as string
        )
        .test('fileSize', messages['static.form.error_file_size'] as string, (value: File | Blob) => {
          if (value instanceof File || value instanceof Blob) {
            return value.size <= 1024 * 1024 * 10 // 10MB
          }
          return false
        }),
    }),
    onSubmit: values => {
      setGetUrls(true)
      dispatch(
        updateQuiz({
          purchaseSerial: values.serialNumber,
          purchaseDate: values.purchaseDate,
          purchaseVendor: values.purchaseStore,
        })
      )
    },
  })

  const resizeImage = async (
    inputBlob: File | Blob,
    callback: (value: File) => void,
    outputFileInfo?: { name: string; type: string } | null
  ) => {
    // Check if outputFileInfo is defined, otherwise provide default values
    const fileInfo = outputFileInfo || { name: '', type: '' }
    Resizer.imageFileResizer(
      inputBlob,
      1800, // Desired width
      1800, // Desired height
      'JPEG', // Format
      85, // Quality
      0, // Rotation
      (uri: string | File | Blob | ProgressEvent<FileReader>) => {
        if (typeof uri === 'string') {
          // Convert the data URI to a Blob
          const byteString = atob(uri.split(',')[1])
          const byteArray = new ArrayBuffer(byteString.length)
          const byteView = new Uint8Array(byteArray)
          for (let i = 0; i < byteString.length; i += 1) {
            byteView[i] = byteString.charCodeAt(i)
          }
          const finalBlob = new Blob([byteArray], { type: fileInfo.type })

          // Callback with the resized Blob
          callback(new File([finalBlob], fileInfo.name, { type: fileInfo.type }))
        } else if (uri instanceof Blob) {
          // If it's already a Blob, just pass it to the callback
          callback(new File([uri], fileInfo.name, { type: fileInfo.type }))
        }
      },
      'blob'
    )
  }

  const resizeAndUploadImage = async (file: File | Blob, callback: (value: File) => void) => {
    if (file.type === 'image/jpeg' || file.type === 'image/png') {
      // Resize and process JPEG/PNG images
      resizeImage(file as File, callback)
    } else {
      // Handle PDF files without resizing
      callback(file as File)
    }
  }
  // When we have the presigned URLs, upload the files
  useEffect(() => {
    if (presignedUrl1.data && presignedUrl2.data) {
      // Uploads must be done in sequence to ensure that the Serial Number file is uploaded first
      // Otherwise netowrk conditions and/or filesize can effect the order of the uploads, causing incorrcet indexing
      if (!uploadSerialRes.isSuccess) {
        uploadSerial({
          file: formik.values.serialFile as File,
          url: presignedUrl1.data.url,
          type: fileTypes.serialNumber,
        })
      }
      if (uploadSerialRes.isSuccess) {
        uploadReceipt({
          file: formik.values.receiptFile as File,
          url: presignedUrl2.data.url,
          type: fileTypes.receipt,
        })
      }
      setGetUrls(false)
    }
  }, [
    presignedUrl1.data,
    presignedUrl2.data,
    uploadSerialRes.isSuccess,
    uploadSerial,
    formik.values.serialFile,
    formik.values.receiptFile,
    uploadReceipt,
  ])

  /**
   * Update the store with the user's purchase details if the request was successful
   */
  useEffect(() => {
    if (uploadSerialRes.isSuccess && uploadReceiptRes.isSuccess) {
      dispatch(
        updateQuiz({
          step: QuizStep.VALUATION,
          prevStep: QuizStep.PURCHASE,
        })
      )
    }
  }, [dispatch, uploadSerialRes, uploadReceiptRes])

  /**
   * Redirect to the next step if the step has changed in the store
   */
  useEffect(() => {
    if (quiz.step === QuizStep.VALUATION) {
      navigate(quizValuationHref)
    }
  }, [navigate, quiz.step, quizValuationHref])

  if (isRedirecting) {
    return <RedirectPartialView />
  }

  return (
    <>
      <Navbar />
      <S.QuizMain>
        <QuizHeader
          title={<FormattedMessage id="quiz_purchased_product_page.content.base.title" />}
          intro={<FormattedMessage id="quiz_purchased_product_page.content.base.intro" />}
          nextStep={<FormattedMessage id="quiz_purchased_product_page.content.base.button_text" />}
          progress={12}
          step={1}
        />
        <S.Form onSubmit={formik.handleSubmit}>
          <S.Field>
            <Flex justify="space-between" wrap>
              <h4>
                <FormattedMessage id="static.page.purchase.serial_title" />
              </h4>
            </Flex>
            <UploadField
              name="serialFile"
              label={
                messages['quiz_purchased_product_page.content.questions.serial_number_file_upload.label'] as string
              }
              placeholder={
                messages[
                  'quiz_purchased_product_page.content.questions.serial_number_file_upload.placeholder'
                ] as string
              }
              accept={allowedFileTypes.join(',')}
              onBlur={formik.handleBlur}
              error={
                Boolean(formik.errors.serialFile) && Boolean(formik.touched.serialFile)
                  ? formik.errors.serialFile
                  : undefined
              }
              onOutputFile={file => {
                if (isValidImage(file)) {
                  resizeAndUploadImage(file, dataBlob => {
                    formik.setFieldValue('serialFile', dataBlob)
                  })
                } else {
                  formik.setFieldError('serialFile', messages['static.form.error_file_invalid'] as string)
                }
              }}
              hint={
                <S.HintButton onClick={() => setShowHelper(!showHelper)}>
                  <Icon icon="material-symbols:info" fontSize={18} />
                  <FormattedMessage id="quiz_purchased_product_page.content.serial_number_dialog.open_dialog" />
                </S.HintButton>
              }
            />
            <TextField
              name="serialNumber"
              label={messages['quiz_purchased_product_page.content.questions.serial_number.label'] as string}
              placeholder={
                messages['quiz_purchased_product_page.content.questions.serial_number.placeholder'] as string
              }
              defaultValue={formik.values.serialNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                Boolean(formik.errors.serialNumber) && Boolean(formik.touched.serialNumber)
                  ? formik.errors.serialNumber
                  : undefined
              }
              mask={{ blocks: [3, 2, 8], delimiter: '-', uppercase: true }}
            />
          </S.Field>
          <S.Field>
            <h4>
              <FormattedMessage id="static.page.purchase.receipt_title" />
            </h4>
            <UploadField
              name="receiptFile"
              label={messages['quiz_purchased_product_page.content.questions.receipt_file_upload.label'] as string}
              placeholder={
                messages['quiz_purchased_product_page.content.questions.receipt_file_upload.placeholder'] as string
              }
              accept={allowedFileTypes.join(',')}
              onBlur={formik.handleBlur}
              error={
                Boolean(formik.errors.receiptFile) && Boolean(formik.touched.receiptFile)
                  ? formik.errors.receiptFile
                  : undefined
              }
              onOutputFile={file => {
                if (isValidImage(file)) {
                  resizeAndUploadImage(file, dataBlob => {
                    formik.setFieldValue('receiptFile', dataBlob)
                  })
                } else {
                  formik.setFieldError('receiptFile', messages['static.form.error_file_invalid'] as string)
                }
              }}
            />
            <S.GroupedFields>
              <TextField
                name="purchaseDate"
                label={messages['quiz_purchased_product_page.content.questions.date_of_purchase.label'] as string}
                placeholder={
                  messages['quiz_purchased_product_page.content.questions.date_of_purchase.placeholder'] as string
                }
                defaultValue={formik.values.purchaseDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.errors.purchaseDate) && Boolean(formik.touched.purchaseDate)
                    ? formik.errors.purchaseDate
                    : undefined
                }
                mask={{ blocks: [2, 2, 4], delimiter: '/' }}
              />
              <SelectField
                name="purchaseStore"
                label={messages['quiz_purchased_product_page.content.questions.place_of_purchase.label'] as string}
                placeholder={
                  messages['quiz_purchased_product_page.content.questions.place_of_purchase.placeholder'] as string
                }
                options={programs[program!].retailers.map(r => ({ value: r, label: r }))}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  Boolean(formik.errors.purchaseStore) && Boolean(formik.touched.purchaseStore)
                    ? formik.errors.purchaseStore
                    : undefined
                }
              />
            </S.GroupedFields>
          </S.Field>
          <S.ActionBar>
            <Tippy
              content={<FormattedMessage id="static.form.hint_continue" />}
              placement="bottom"
              disabled={formik.isValid}>
              <div>
                <Button
                  type="submit"
                  disabled={
                    !formik.isValid || formik.isSubmitting || uploadSerialRes.isLoading || uploadReceiptRes.isLoading
                  }>
                  {formik.isSubmitting || uploadSerialRes.isLoading || uploadReceiptRes.isLoading ? (
                    <Spinner />
                  ) : (
                    <FormattedMessage id="quiz_purchased_product_page.content.base.button_text" />
                  )}
                </Button>
              </div>
            </Tippy>
          </S.ActionBar>
        </S.Form>
      </S.QuizMain>
      <SerialNumberHelper show={showHelper} onExit={() => setShowHelper(false)} />
      <Footer />
    </>
  )
}

export default PurchaseCheck
